import { useDateFormatter, useTranslator } from '@bluebeam/react-i18n';
import classnames from 'classnames';
import Cal24 from 'components/Icons/Calendar24';
import OverlaySection from 'components/Overlays/OverlaySection';
import HeavyLabel from 'components/Overlays/OverlaySection/OverlaySectionHeavyLabel';
import OfficialQuestionLabelStatus from 'components/Overlays/RFIOverlays/OfficialQuestionLabelStatus';
import RFIOverlayAttachments from 'components/Overlays/RFIOverlays/RFIOverlayAttachments';
import React from 'react';
import { ImpactCategories } from 'records/RFI';
import RfiVersionListDropdown from './RfiVersionListDropdown';
import { useViewRfiMachine } from './ViewRFIMachine';

interface Props {
  onRfiSelection: (rfiid: string) => void;
}

const ViewOfficialQuestionSection: React.FC<Props> = (props) => {
  const { translate } = useTranslator(['mapView', 'rfiPage']);
  const dateFormatter = useDateFormatter({
    dateStyle: 'long',
  });

  const { viewRfiMachineState } = useViewRfiMachine();
  const { onRfiSelection } = props;

  const { rfi, rfiAttachmentSheetVersions, rfiVersionList } =
    viewRfiMachineState.context;

  const assignee = rfi.assignees.get(0)?.email;

  const distroList =
    rfi.distributionlist.size > 0
      ? rfi.distributionlist.toArray().reduce((acc, u, i) => {
          return `${acc}${i > 0 ? ', ' : ''}${u}`;
        }, '')
      : translate('mapView:viewOfficialQuestionSection.none');

  const status = rfi.answer ? 'Closed' : 'Open';
  const sectionlabel = (
    <div className="row justify-between items-center w100">
      <div>
        {translate('mapView:viewOfficialQuestionSection.officialQuestion')}
      </div>
      <OfficialQuestionLabelStatus status={status} />
    </div>
  );

  const getDueDate = (): string => {
    if (rfi.duedate === 0) {
      return translate('mapView:viewOfficialQuestionSection.noDueDate');
    }

    return dateFormatter?.format(new Date(rfi.duedate * 1000));
  };

  const impactCategories: ImpactCategories = {
    NONE: translate('rfiPage:none'),
    COST: translate('rfiPage:cost'),
    SCHEDULE: translate('rfiPage:schedule'),
    COST_AND_SCHEDULE: translate('rfiPage:costAndSchedule'),
  };

  return (
    <OverlaySection label={sectionlabel}>
      <div className="row items-center medium font-size-7 black-2 mb3">
        <div className="mr2">
          {translate('mapView:postRFIListItem.rfi')} {rfi.number}
          {rfi.versionnumber ? `.${rfi.versionnumber}` : null}
        </div>
        {rfiVersionList.size > 1 && (
          <RfiVersionListDropdown
            rfis={rfiVersionList}
            currentRfiId={rfi.id}
            onRfiSelection={onRfiSelection}
          />
        )}
      </div>
      <div className="mb3">
        <HeavyLabel
          label={translate('mapView:viewOfficialQuestionSection.subject')}
        />
        <div>{rfi.title}</div>
      </div>
      <div className="mb3">
        <HeavyLabel
          label={translate('mapView:viewOfficialQuestionSection.question')}
        />
        <div>{rfi.question}</div>
      </div>
      <div className="mb3">
        <HeavyLabel
          label={translate('mapView:viewOfficialQuestionSection.assignee')}
        />
        <div>{assignee}</div>
      </div>
      <div className="mb3">
        <HeavyLabel
          label={translate(
            'mapView:viewOfficialQuestionSection.distributionList'
          )}
        />
        <div>{distroList}</div>
      </div>
      <div className="mb3">
        <HeavyLabel
          label={translate('mapView:viewOfficialQuestionSection.dueDate')}
        />
        <div className="row items-center">
          {rfi.answer ? (
            <span>
              {translate('mapView:viewOfficialQuestionSection.closed')}
            </span>
          ) : (
            <span
              className={classnames('row items-center', {
                'color-error': dateFormatter?.format(
                  new Date(rfi.duedate * 1000)
                ),
              })}
            >
              <Cal24 />
              <span className="ml1">{getDueDate()}</span>
            </span>
          )}
        </div>
      </div>
      <div className="mb3">
        <HeavyLabel
          label={translate('mapView:viewOfficialQuestionSection.impact')}
        />
        <div>
          {rfi.impactcategory.display === 'None'
            ? translate('rfiPage:rfiDetails.noImpact')
            : impactCategories[rfi.impactcategory.kind]}
        </div>
      </div>
      <RFIOverlayAttachments
        label={translate('mapView:viewOfficialQuestionSection.attachments')}
        projectId={rfi.projectid}
        attachments={rfi.attachments}
        attachedSheetversions={rfiAttachmentSheetVersions}
      />
    </OverlaySection>
  );
};

export default ViewOfficialQuestionSection;
